import React, { ReactNode, useMemo } from "react"
import { Box, Grid, Skeleton, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useMoney } from "../../../modules/currency/useMoney"
import { isDinero, toDinero } from "../../../modules/currency/currencyLib"
import { MonetaryAmount } from "../../../modules/currency/currencyTypes"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { add, Dinero } from "dinero.js"
import { tailwindPalette } from "../../../modules/theme/tailwindPalette"
import { TranslationKey } from "../../../modules/localization/translations"

type TableSummaryWrapProps = {
    children: ReactNode
    padding?: "sm" | "m" | "lg"
}

const paddingMap = {
    sm: "1rem",
    m: "1.5rem",
    lg: "2rem",
}

const useStyles = makeStyles<{ padding: TableSummaryWrapProps["padding"] }>()((_theme, { padding }) => ({
    container: {
        padding: padding ? paddingMap[padding] : paddingMap.m,
    },
}))

export const TableSummaryWrap: React.FC<TableSummaryWrapProps> = ({ children, padding }) => {
    const { classes } = useStyles({ padding })
    return (
        <Grid order={1}>
            <Grid container gap={1.5} className={classes.container} justifyContent="flex-end">
                {children}
            </Grid>
        </Grid>
    )
}

type TableSummaryBoxProps = {
    title: string
    summary: string | number | undefined
    variant?: "default" | "embedded"
    color: "blue" | "green" | "yellow"
}

const useStylesSummaryBox = makeStyles<{ color: string }>()((theme, { color }) => ({
    boxWrap: {
        textAlign: "right",
        borderRadius: "0.5rem",
        backgroundColor: color,
        padding: "0.4rem 1.5rem 0.75rem",
    },
    caption: {
        fontSize: "0.625rem",
        lineHeight: 1,
        color: tailwindPalette.slate[900],
    },
    infoSkeleton: {
        paddingTop: "0.5rem",
        fontSize: theme.typography.h6.fontSize,
    },
    info: {
        lineHeight: 1,
        fontWeight: 400,
        paddingTop: "0.5rem",
        color: tailwindPalette.slate[700],
    },
    boxWrapEmbedded: {
        textAlign: "right",
    },
    infoEmbedded: {
        lineHeight: 1,
        fontWeight: 600,
        paddingTop: "0.5rem",
        color: tailwindPalette.slate[700],
    },
    divider: {
        width: "100%",
        backgroundColor: color,
        height: "0.375rem",
        borderRadius: "0.125rem",
    },
}))

export const TableSummaryBox: React.FC<TableSummaryBoxProps> = ({ title, summary, color, variant = "default" }) => {
    const accentColor = {
        blue: tailwindPalette.sky[200],
        green: tailwindPalette.green[50],
        yellow: tailwindPalette.amber[200],
    }[color]

    const { classes } = useStylesSummaryBox({ color: accentColor })

    if (variant === "embedded") {
        return (
            <Box className={classes.boxWrapEmbedded}>
                <Box className={classes.divider} />
                <Typography variant="caption" className={classes.caption}>
                    {title}
                </Typography>
                {typeof summary === "undefined" ? (
                    <Skeleton variant="text" className={classes.infoSkeleton} />
                ) : (
                    <Typography variant="h6" className={classes.infoEmbedded}>
                        {summary}
                    </Typography>
                )}
            </Box>
        )
    }

    if (variant === "default") {
        return (
            <Box className={classes.boxWrap}>
                <Typography variant="caption" className={classes.caption}>
                    {title}
                </Typography>
                {typeof summary === "undefined" ? (
                    <Skeleton variant="text" className={classes.infoSkeleton} />
                ) : (
                    <Typography variant="h6" className={classes.info}>
                        {summary}
                    </Typography>
                )}
            </Box>
        )
    }

    return null
}

type TableTotalAmountBoxProps<T> = {
    data: T[] | undefined
    isLoading?: boolean
    accessor: (item: T) => MonetaryAmount | Dinero<number> | null | undefined
    variant?: "default" | "embedded"
    localizedKey?: TranslationKey
}

export const TableTotalAmountBox = <T,>({
    data,
    isLoading = false,
    accessor,
    variant = "default",
    localizedKey = "table-summary-box-total-period",
}: TableTotalAmountBoxProps<T>) => {
    const moneyFactory = useMoney({})
    const isValid = !isLoading && data !== undefined

    const total = useMemo(() => {
        // Project runs with an older version of Typescript that doesn't type filter properly
        const values =
            data
                ?.map(accessor)
                .filter((v) => v !== null)
                .filter((v) => v !== undefined) ?? []
        const isParsed = values.some((v) => isDinero(v))
        const dineros = isParsed ? (values as Dinero<number>[]) : (values as MonetaryAmount[]).map(toDinero)

        if (dineros.length === 0) return undefined

        return dineros.reduce((prev, cur) => add(prev, cur))
    }, [data, accessor])

    // NOTE: Show skeleton while loading instead of a potentially wrong amount
    const formattedSummary = isValid ? (total ? moneyFactory.format(total) : "0") : undefined

    return (
        <LocalizedStrict id={localizedKey} attrs={{ title: true }}>
            <TableSummaryBox title="Total this period" variant={variant} summary={formattedSummary} color="green" />
        </LocalizedStrict>
    )
}

export const TableTotalAmountFromAPIBox = ({ totalAmount }: { totalAmount?: MonetaryAmount }) => {
    const moneyFactory = useMoney({})

    if (!totalAmount) {
        return null
    }

    const formattedSummary = moneyFactory.formatAsNumber(toDinero(totalAmount))

    return (
        <LocalizedStrict id="table-summary-box-total-period" attrs={{ title: true }}>
            <TableSummaryBox title="Total this period" summary={formattedSummary} color="green" />
        </LocalizedStrict>
    )
}

type TableNumberOfItemsBoxProps = {
    data: unknown[] | undefined
    /** Prevents displaying stale/incorrect counts during data loading. When true, shows loading state instead of cached data count. */
    isLoading?: boolean
    title: string
}

/**
 * @param data - Use table.getFilteredRowModel().rows to get the current filtered/paginated rows
 * @param isLoading - Pass the query's isLoading state to prevent showing stale counts during refetch
 * @param title - Localized title for the summary box
 *
 * renderBottomToolbarCustomActions={({ table }) => (
 *     <TableSummaryWrap>
 *         <TableNumberOfItemsBox
 *             data={table.getFilteredRowModel().rows}
 *             isLoading={queryResult.isLoading}
 *             title="text"
 *         />
 *         <TableTotalAmountBox
 *             data={table.getFilteredRowModel().rows}
 *             isLoading={queryResult.isLoading}
 *             accessor={(row) => row.original.amount}
 *         />
 *     </TableSummaryWrap>
 * )}
 */
export const TableNumberOfItemsBox: React.FC<TableNumberOfItemsBoxProps> = ({ data, isLoading = false, title }) => {
    const itemCount = useMemo(() => (isLoading ? undefined : data?.length), [data, isLoading])

    return <TableSummaryBox title={title} summary={itemCount} color="blue" />
}
